// --- Dependencies -------------------------------------------------------------------------------
import { useNavigate } from "react-router-dom";
// --- Our Modules --------------------------------------------------------------------------------
import useAuth from "@/hooks/useAuth";
import type { TyAccount } from "@/hooks/useAuth/types";
import useFetch from "@/hooks/useFetch";
import useStore from "@/hooks/useStore";
import useTranslation from "@/hooks/useTranslation";
import "./styles.css";

// --- Types --------------------------------------------------------------------------------------

type Props = {
    onClose: () => void;
};

// --- Logic --------------------------------------------------------------------------------------

/**
 * Logging out is the expensive path — it costs an OTP to undo. So before confirming, this offers
 * every cheaper alternative first: switch to an account already on the device, or add a new one.
 */
export default function LogoutPopup({ onClose }: Props) {
    const t = useTranslation();
    const navigate = useNavigate();

    const client = useAuth((state) => state.client);
    const accounts = useAuth((state) => state.accounts);
    const switchAccount = useAuth((state) => state.switchAccount);
    const removeAccount = useAuth((state) => state.removeAccount);
    const startAddingAccount = useAuth((state) => state.startAddingAccount);
    const clearStore = useStore((state) => state.clearStore);

    const { invoke: revokeToken } = useFetch("auth/logout", { method: "DELETE" }, false);

    const others = accounts.filter((account) => account.client._id !== client?._id);

    const handleSwitch = (account: TyAccount) => {
        onClose();
        switchAccount(account.client._id);
        clearStore();
        window.location.replace("/");
    };

    const handleAddAccount = () => {
        onClose();
        startAddingAccount();
        navigate("/login");
    };

    const handleLogout = () => {
        // Revoke this device's token server-side before dropping it locally.
        revokeToken();
        const remaining = others;
        removeAccount(client?._id ?? "");
        clearStore();
        onClose();
        // Another identity is still signed in here, so land on the dashboard instead of /login.
        window.location.replace(remaining.length > 0 ? "/" : "/login");
    };

    return (
        <div className="logout-popup">
            <div className="logout-popup-icon">
                <img src="/icons/logout-icon.svg" alt="" width={20} height={20} />
            </div>

            <p className="logout-popup-message">{t.profile.logoutConfirmMessage}</p>

            {others.length > 0 && (
                <div className="logout-popup-switch">
                    <p className="logout-popup-switch-hint">{t.profile.logoutSwitchHint}</p>
                    <div className="logout-popup-accounts">
                        {others.map((account) => (
                            <button key={account.client._id} type="button" className="logout-popup-account" onClick={() => handleSwitch(account)}>
                                <div className="logout-popup-account-avatar">
                                    {account.client.picture ? (
                                        <img src={account.client.picture} alt={account.client.name} />
                                    ) : (
                                        <img
                                            className="logout-popup-account-placeholder"
                                            src="/icons/client-small-placeholder-icon.svg"
                                            alt={account.client.name}
                                        />
                                    )}
                                </div>
                                <div className="logout-popup-account-info">
                                    <p className="logout-popup-account-name">{account.client.name}</p>
                                    <p className="logout-popup-account-phone" dir="ltr">
                                        {account.client.phone}
                                    </p>
                                </div>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            <button type="button" className="logout-popup-add" onClick={handleAddAccount}>
                <img src="/icons/plus-brand-icon.svg" alt="" width={12} height={12} />
                <span>{t.profile.logoutAddAccount}</span>
            </button>

            <div className="logout-popup-actions">
                <button type="button" className="logout-popup-cancel" onClick={onClose}>
                    {t.profile.logoutCancel}
                </button>
                <button type="button" className="logout-popup-confirm" onClick={handleLogout}>
                    {t.profile.logoutConfirmAction}
                </button>
            </div>
        </div>
    );
}
